
import Vue, { PropOptions } from 'vue';
import _ from 'lodash';
import { numberAbbreviation } from '@/helpers/numberAbbreviation';
import { numFormat } from '@/helpers/numFormat';
import { productAlias } from '@/helpers/productAlias';
import { customerAlias } from '@/helpers/customerAlias';
import { SalesComparison } from '@/models/page-models/page-items/SalesComparison';

interface YearQuantities {
  q1: number;
  q2: number;
  q3: number;
  q4: number;
  totalSum: number;
}

interface ComparisonItem {
  itemId: number | string;
  product?: any;
  customer?: any;
  previousYearQuantities: YearQuantities;
  currentYearQuantities: YearQuantities;
}

interface GroupedComparisonData {
  groupName: string;
  previousYearQuantities: YearQuantities;
  currentYearQuantities: YearQuantities;
  subItemsComparisonData: ComparisonItem[];
}

export default Vue.extend({
  props: {
    input: { type: Object, default: () => {} } as PropOptions<SalesComparison>,
    getVariable: { type: Function, default: () => () => {} },
  },
  data() {
    return {
      quarters: ['q1', 'q2', 'q3', 'q4', 'totalSum'] as Array<keyof YearQuantities>,
    };
  },
  computed: {
    comparisonData(): any {
      return this.getVariable(this.input.comparisonDataVariable) || { salesComparison: [], year: new Date().getFullYear() };
    },
    isCustomerMode(): boolean {
      return this.input.mode === 'customer';
    },
    groupedComparisonData(): GroupedComparisonData[] {
      const list: ComparisonItem[] = _.get(this.comparisonData, 'salesComparison', []);
      
      const groupIteratee = this.isCustomerMode 
        ? (item: ComparisonItem) => _.get(item, 'customer.groups.0.name', '-') 
        : 'product.productLine';

      const groupedItems = _.groupBy(list, groupIteratee);

      const groups = _.map(Object.keys(groupedItems), (groupName) => {
        const subItems = _.orderBy(groupedItems[groupName], ['previousYearQuantities.totalSum'], ['desc']);
        return {
          groupName,
          previousYearQuantities: this.sumQuantities(subItems.map(i => i.previousYearQuantities)),
          currentYearQuantities: this.sumQuantities(subItems.map(i => i.currentYearQuantities)),
          subItemsComparisonData: subItems
        };
      });
      return _.orderBy(groups, ['previousYearQuantities.totalSum'], ['desc']);
    },
    displayInSAR(): boolean {
      return this.getVariable(this.input.displaySarVariable) || false;
    },
    grandTotals(): { previous: YearQuantities; current: YearQuantities } {
      const groups = this.groupedComparisonData;
      return {
        previous: this.sumQuantities(groups.map(g => g.previousYearQuantities)),
        current: this.sumQuantities(groups.map(g => g.currentYearQuantities)),
      };
    }
  },
  methods: {
    // --- Display Helpers ---
    getQuarterLabel(q: string, year: number): string {
      if (q === 'totalSum') return 'Total';
      return `${q.replace('q', '')}Q${year % 100}`;
    },
    getItemName(item: ComparisonItem): string {
      return this.isCustomerMode ? customerAlias(item.customer) : productAlias(item.product);
    },
    getItemUnit(item: ComparisonItem): string {
      return this.isCustomerMode ? 'SAR' : _.get(item, 'product.packingMode.0.unit1', '-');
    },
    getItemLink(item: ComparisonItem): string | object {
      if (this.isCustomerMode) {
        return {
          path: '/customer-sales-comparison',
          query: { customer: item.itemId, year: this.comparisonData.year }
        };
      }
      return `/products/${item.itemId}`;
    },

    // --- Calculation Helpers ---
    sumQuantities(list: YearQuantities[]): YearQuantities {
      // Helper to sum a specific key across the array
      const sumKey = (key: keyof YearQuantities) => 
        list.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);

      return {
        q1: sumKey('q1'),
        q2: sumKey('q2'),
        q3: sumKey('q3'),
        q4: sumKey('q4'),
        totalSum: sumKey('totalSum'),
      };
    },

    calculatePercentage(prev: number, curr: number): string {
      const p = Number(prev || 0);
      const c = Number(curr || 0);
      
      if (p === 0 && c === 0) return '0%';
      if (p === 0) return '100%';
      
      const percent = ((c - p) / p) * 100;
      return `${Math.round(percent)}%`;
    },

    showQuantityOrSAR(val: number | string): string | number {
      const isCurrency = this.displayInSAR || this.isCustomerMode;
      const numVal = Number(val || 0);
      return isCurrency ? numFormat(numVal, true) : numberAbbreviation(numVal);
    }
  }
});
